import { useStore } from '@nanostores/react';
import { memo, useState, useEffect } from 'react';
import { MenuSection } from './MenuSection';
import { SettingsDialogContent } from './SettingsDialogContent';
import { getInitialPage } from './helpers';
import { UpdatePlanScreenDialog } from '~/components/dialogs/UpdatePlanScreenDialog/UpdatePlanScreenDialog';
import { Dialog, HiddenDialogTitle } from '~/components/ui/Dialog';
import * as Tooltip from '~/components/ui/Tooltip';
import { DatabaseManager } from '~/components/ui/shadcn/components/supabase-manager/Database.client';
import { SheetNavigationProvider } from '~/lib/context/sheetNavigation';
import { useSettingsMenuConfigs } from '~/lib/hooks/useSettingsMenuConfigs';
import { chatStore } from '~/lib/stores/chat';
import { SettingsPage } from '~/lib/stores/menu';
import { pricingStore } from '~/lib/stores/pricing/pricing';
import { projectStore } from '~/lib/stores/project';
import { userStore } from '~/lib/stores/user';

interface SettingsDialogContent {
  isOpen?: boolean;
  initialPage?: SettingsPage;
  screen?: string;
  closeDialog: () => void;
}

export const SettingsDialog = memo<SettingsDialogContent>(({ isOpen, initialPage, screen, closeDialog }) => {
  const [currentPage, setCurrentPage] = useState<SettingsPage>(() => getInitialPage(initialPage));

  const activeOrganization = useStore(userStore.getActiveOrganization());
  const updatePlanScreenData = useStore(pricingStore.updatePlanScreenData);
  const chatStarted = useStore(chatStore.started);
  const permissions = useStore(projectStore.permissions);

  const { projectSectionConfig, personalSectionConfig, teamSectionConfig } = useSettingsMenuConfigs({
    activeOrganization,
  });

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(getInitialPage(initialPage));
    }
  }, [isOpen, initialPage]);

  const settingsMenu = (
    <>
      {chatStarted && permissions.canOpenProjectSettings && (
        <>
          <MenuSection
            config={projectSectionConfig}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            titleClassName={chatStarted && permissions.canOpenProjectSettings ? 'p-2 md:p-4' : ''}
          />
          <hr className="border-bolt-ds-borderDivider" />
        </>
      )}

      <MenuSection
        config={personalSectionConfig}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        activeOrganization={activeOrganization}
        titleClassName={chatStarted && permissions.canOpenProjectSettings ? '' : 'p-2 md:p-4'}
      />

      {!!activeOrganization && (
        <>
          <hr className="border-bolt-ds-borderDivider" />
          <MenuSection config={teamSectionConfig} currentPage={currentPage} setCurrentPage={setCurrentPage} />
        </>
      )}
    </>
  );

  return (
    <Dialog
      open={!!isOpen}
      onClose={closeDialog}
      maxWidth={1500}
      height={700}
      contentClassName="max-w-md lg:max-w-none h-[650px] max-h-[85vh] flex"
      closeButtonClassName="right-2"
    >
      <UpdatePlanScreenDialog open={!!updatePlanScreenData} />
      <HiddenDialogTitle>Settings</HiddenDialogTitle>

      <SheetNavigationProvider
        initialStack={[
          {
            title: 'Tables',
            component: <DatabaseManager />,
          },
        ]}
      >
        <>
          <aside className="h-full overflow-y-auto flex flex-col max-w-80 w-12 md:w-auto md:min-w-1/8 bg-bolt-ds-surface border-r border-bolt-ds-borderDivider p-2 md:p-4 pt-0 md:pt-0">
            <div className="flex-1 flex flex-col gap-4">{settingsMenu}</div>
          </aside>

          <Tooltip.Provider>
            <SettingsDialogContent currentPage={currentPage} closeDialog={closeDialog} screen={screen} />
          </Tooltip.Provider>
        </>
      </SheetNavigationProvider>
    </Dialog>
  );
});

SettingsDialog.displayName = 'SettingsDialog';
